import { FC, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { profileSelector } from 'selectors'
import { createInstance, OptimizelyProvider, useDecision } from '@optimizely/react-sdk'
import type { UserAttributes } from '@optimizely/optimizely-sdk'

export const optimizely = createInstance({
  sdkKey: import.meta.env.REACT_APP_OPTIMIZELY_KEY,
})

type OptimizelyProfile = { id?: number | string; [key: string]: unknown } | null | undefined

export function isFeatureEnabled(flag: string, profile?: OptimizelyProfile): boolean {
  const userId = profile?.id != null ? String(profile.id) : undefined
  const attributes = profile ? (profile as UserAttributes) : undefined
  return optimizely.decide(flag, [], userId, attributes).enabled
}

type FeatureRenderer = (enabled: boolean, variables: Record<string, unknown>) => ReactNode
type FeatureProps = {
  feature: string
  children: ReactNode | FeatureRenderer
}

function renderIfEnabled(children: ReactNode): FeatureRenderer {
  return (enabled) => (enabled ? children : <></>)
}

export const FeatureFlag: FC<FeatureProps> = (props) => {
  const { feature, children } = props
  const { profile } = useSelector(profileSelector)

  const renderer = typeof children === 'function' ? children : renderIfEnabled(children)

  if (!profile) {
    return <>{renderer(false, {})}</>
  }

  return (
    <OptimizelyProvider optimizely={optimizely} user={{ id: String(profile.id), attributes: { ...profile } }}>
      <Decision feature={feature}>{renderer}</Decision>
    </OptimizelyProvider>
  )
}

const Decision: FC<{
  feature: string
  children: FeatureRenderer
}> = (props) => {
  const { feature, children } = props
  const [decision, clientReady, didTimeout] = useDecision(feature)

  // This is to avoid treating the decision as disabled while the SDK is still
  // loading. It's possible to navigate to a page before the SDK is ready which
  // will cause feature flags to be treated as disabled.
  if (!clientReady && !didTimeout) {
    return null
  }

  return <>{children(decision.enabled, decision.variables)}</>
}
